import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  alpha,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Store,
  Category,
  Inventory,
  Widgets,
  TrendingUp,
  ArrowForward,
  CheckCircle,
  Add,
  Sync,
  CloudUpload,
  Settings,
  PlayArrow,
  Lightbulb,
  Speed,
  Security,
  Refresh,
} from '@mui/icons-material';
import StatsCard from '../components/common/StatsCard';
import PageHeader from '../components/common/PageHeader';
import { StatsSkeleton, CardSkeleton } from '../components/common/LoadingState';
import api from '../services/api';

const quickActions = [
  {
    title: 'Nuevo Retail',
    description: 'Agrega una nueva tienda',
    icon: <Store />,
    path: '/retails',
    color: 'primary',
  },
  {
    title: 'Crear Widget',
    description: 'Configura upselling',
    icon: <Widgets />,
    path: '/widgets/new',
    color: 'secondary',
  },
  {
    title: 'Importar Specs',
    description: 'Carga especificaciones',
    icon: <CloudUpload />,
    path: '/specs/import',
    color: 'success',
  },
  {
    title: 'Configuración',
    description: 'Ajustes del sistema',
    icon: <Settings />,
    path: '/config',
    color: 'warning',
  },
];

const steps = [
  {
    number: 1,
    title: 'Configura Retails',
    description: 'Agrega las tiendas con sus URLs de feed XML',
    icon: <Store />,
    completed: false,
  },
  {
    number: 2,
    title: 'Crea Categorías',
    description: 'Organiza productos por categorías',
    icon: <Category />,
    completed: false,
  },
  {
    number: 3,
    title: 'Importa Productos',
    description: 'Carga productos y sus especificaciones',
    icon: <Inventory />,
    completed: false,
  },
  {
    number: 4,
    title: 'Configura Widgets',
    description: 'Crea widgets de upselling para cada retail',
    icon: <Widgets />,
    completed: false,
  },
];

const features = [
  {
    icon: <Speed />,
    title: 'Alto Rendimiento',
    description: 'Sincronización rápida de feeds XML',
  },
  {
    icon: <Security />,
    title: 'Matching Preciso',
    description: 'Reglas personalizables por retail',
  },
  {
    icon: <Lightbulb />,
    title: 'IA Integrada',
    description: 'Ordenamiento automático de productos',
  },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    retails: 0,
    categories: 0,
    products: 0,
    widgets: 0,
  });
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [retails, categories, products, widgets] = await Promise.all([
          api.get('/retails'),
          api.get('/categories'),
          api.get('/products'),
          api.get('/widgets'),
        ]);

        setStats({
          retails: retails.data.length,
          categories: categories.data.length,
          products: products.data.length,
          widgets: widgets.data.length,
        });

        // Update steps completion based on data
        steps[0].completed = retails.data.length > 0;
        steps[1].completed = categories.data.length > 0;
        steps[2].completed = products.data.length > 0;
        steps[3].completed = widgets.data.length > 0;

      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const completedSteps = steps.filter(s => s.completed).length;

  return (
    <Box>
      <PageHeader
        title="Dashboard"
        subtitle="Bienvenido a Omnitok Upselling"
      >
        <Tooltip title="Refrescar datos">
          <IconButton onClick={() => window.location.reload()}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </PageHeader>

      {/* Stats Section */}
      {loading ? (
        <StatsSkeleton count={4} />
      ) : (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Retails"
              value={stats.retails}
              icon={<Store />}
              color="primary"
              subtitle="Tiendas activas"
              onClick={() => navigate('/retails')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Categorías"
              value={stats.categories}
              icon={<Category />}
              color="secondary"
              subtitle="Organizaciones"
              onClick={() => navigate('/categories')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Productos"
              value={stats.products}
              icon={<Inventory />}
              color="success"
              subtitle="En catálogo"
              onClick={() => navigate('/products')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Widgets"
              value={stats.widgets}
              icon={<Widgets />}
              color="warning"
              subtitle="Configurados"
              onClick={() => navigate('/widgets')}
            />
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Acciones Rápidas
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Accesos directos a las funciones principales
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={2}>
                {quickActions.map((action, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box
                      onClick={() => navigate(action.path)}
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        '&:hover': {
                          borderColor: `${action.color}.main`,
                          backgroundColor: (theme) => alpha(theme.palette[action.color].main, 0.04),
                          transform: 'translateY(-2px)',
                          boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette[action.color].main, 0.15)}`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: (theme) => alpha(theme.palette[action.color].main, 0.1),
                          color: `${action.color}.main`,
                        }}
                      >
                        {action.icon}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {action.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {action.description}
                        </Typography>
                      </Box>
                      <ArrowForward sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Getting Started */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Primeros Pasos
                </Typography>
                <Chip
                  label={`${completedSteps}/${steps.length}`}
                  size="small"
                  color={completedSteps === steps.length ? 'success' : 'primary'}
                  sx={{ fontWeight: 600 }}
                />
              </Box>

              {/* Progress bar */}
              <Box
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  mb: 3,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${(completedSteps / steps.length) * 100}%`,
                    borderRadius: 3,
                    background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)',
                    transition: 'width 0.5s ease-in-out',
                  }}
                />
              </Box>

              <List sx={{ py: 0 }}>
                {steps.map((step, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      px: 0,
                      py: 1.5,
                      opacity: step.completed ? 0.7 : 1,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: step.completed
                            ? (theme) => alpha(theme.palette.success.main, 0.1)
                            : (theme) => alpha(theme.palette.grey[500], 0.1),
                          color: step.completed ? 'success.main' : 'text.secondary',
                        }}
                      >
                        {step.completed ? (
                          <CheckCircle sx={{ fontSize: 16 }} />
                        ) : (
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {step.number}
                          </Typography>
                        )}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={step.title}
                      secondary={step.description}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: 600,
                        sx: { textDecoration: step.completed ? 'line-through' : 'none' },
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* How it Works */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TrendingUp sx={{ color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Cómo Funciona
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                El orquestador permite configurar widgets de upselling que muestran
                productos relacionados en los sitios de retail.
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Los productos se obtienen de feeds XML y las especificaciones técnicas
                de APIs externas. El sistema hace match entre los productos del catálogo
                y los del feed usando reglas de matching configurables (SKU, MPN, etc.).
              </Typography>

              <Divider sx={{ my: 2.5 }} />

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {features.map((feature, index) => (
                  <Box
                    key={index}
                    sx={{
                      flex: '1 1 140px',
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                      textAlign: 'center',
                    }}
                  >
                    <Box sx={{ color: 'primary.main', mb: 1 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Help & Documentation */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: 'white',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                ¿Necesitas Ayuda?
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 3 }}>
                Explora la documentación completa para aprovechar al máximo
                todas las funcionalidades del orquestador.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<PlayArrow />}
                  sx={{
                    backgroundColor: alpha('#ffffff', 0.2),
                    '&:hover': {
                      backgroundColor: alpha('#ffffff', 0.3),
                    },
                  }}
                >
                  Ver Tutorial
                </Button>
                <Button
                  variant="outlined"
                  sx={{
                    borderColor: alpha('#ffffff', 0.5),
                    color: 'white',
                    '&:hover': {
                      borderColor: '#ffffff',
                      backgroundColor: alpha('#ffffff', 0.1),
                    },
                  }}
                >
                  Documentación
                </Button>
              </Box>

              {/* Decorative elements */}
              <Box
                sx={{
                  position: 'absolute',
                  right: -40,
                  top: -40,
                  width: 160,
                  height: 160,
                  borderRadius: '50%',
                  backgroundColor: alpha('#ffffff', 0.1),
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
